/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import { WebPubSub } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as coreRestPipeline from "@azure/core-rest-pipeline";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import { GeneratedClient } from "../generatedClient.js";
import {
  WebPubSubGroupMember,
  WebPubSubListConnectionsInGroupNextOptionalParams,
  WebPubSubListConnectionsInGroupOptionalParams,
  WebPubSubListConnectionsInGroupResponse,
  AddToGroupsRequest,
  WebPubSubAddConnectionsToGroupsOptionalParams,
  WebPubSubCloseAllConnectionsOptionalParams,
  WebPubSubGenerateClientTokenOptionalParams,
  WebPubSubGenerateClientTokenResponse,
  RemoveFromGroupsRequest,
  WebPubSubRemoveConnectionsFromGroupsOptionalParams,
  ContentType,
  WebPubSubSendToAll$binaryOptionalParams,
  WebPubSubSendToAll$textOptionalParams,
  WebPubSubCloseConnectionOptionalParams,
  WebPubSubConnectionExistsOptionalParams,
  WebPubSubSendToConnection$binaryOptionalParams,
  WebPubSubSendToConnection$textOptionalParams,
  WebPubSubRemoveConnectionFromAllGroupsOptionalParams,
  WebPubSubGroupExistsOptionalParams,
  WebPubSubCloseGroupConnectionsOptionalParams,
  WebPubSubSendToGroup$binaryOptionalParams,
  WebPubSubSendToGroup$textOptionalParams,
  WebPubSubRemoveConnectionFromGroupOptionalParams,
  WebPubSubAddConnectionToGroupOptionalParams,
  WebPubSubPermission,
  WebPubSubRevokePermissionOptionalParams,
  WebPubSubCheckPermissionOptionalParams,
  WebPubSubGrantPermissionOptionalParams,
  WebPubSubUserExistsOptionalParams,
  WebPubSubCloseUserConnectionsOptionalParams,
  WebPubSubSendToUser$binaryOptionalParams,
  WebPubSubSendToUser$textOptionalParams,
  WebPubSubRemoveUserFromAllGroupsOptionalParams,
  WebPubSubRemoveUserFromGroupOptionalParams,
  WebPubSubAddUserToGroupOptionalParams,
  WebPubSubListConnectionsInGroupNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing WebPubSub operations. */
export class WebPubSubImpl implements WebPubSub {
  private readonly client: GeneratedClient;

  /**
   * Initialize a new instance of the class WebPubSub class.
   * @param client Reference to the service client
   */
  constructor(client: GeneratedClient) {
    this.client = client;
  }

  /**
   * List connections in a group.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param group Target group name, whose length should be greater than 0 and less than 1025.
   * @param options The options parameters.
   */
  public listConnectionsInGroup(
    hub: string,
    group: string,
    options?: WebPubSubListConnectionsInGroupOptionalParams,
  ): PagedAsyncIterableIterator<WebPubSubGroupMember> {
    const iter = this.listConnectionsInGroupPagingAll(hub, group, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listConnectionsInGroupPagingPage(
          hub,
          group,
          options,
          settings,
        );
      },
    };
  }

  private async *listConnectionsInGroupPagingPage(
    hub: string,
    group: string,
    options?: WebPubSubListConnectionsInGroupOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<WebPubSubGroupMember[]> {
    let result: WebPubSubListConnectionsInGroupResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listConnectionsInGroup(hub, group, options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listConnectionsInGroupNext(
        hub,
        group,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listConnectionsInGroupPagingAll(
    hub: string,
    group: string,
    options?: WebPubSubListConnectionsInGroupOptionalParams,
  ): AsyncIterableIterator<WebPubSubGroupMember> {
    for await (const page of this.listConnectionsInGroupPagingPage(
      hub,
      group,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Add filtered connections to multiple groups.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param groupsToAdd Target groups and connection filter.
   * @param options The options parameters.
   */
  addConnectionsToGroups(
    hub: string,
    groupsToAdd: AddToGroupsRequest,
    options?: WebPubSubAddConnectionsToGroupsOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { hub, groupsToAdd, options },
      addConnectionsToGroupsOperationSpec,
    );
  }

  /**
   * Close the connections in the hub.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param options The options parameters.
   */
  closeAllConnections(
    hub: string,
    options?: WebPubSubCloseAllConnectionsOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { hub, options },
      closeAllConnectionsOperationSpec,
    );
  }

  /**
   * Generate token for the client to connect Azure Web PubSub service.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param options The options parameters.
   */
  generateClientToken(
    hub: string,
    options?: WebPubSubGenerateClientTokenOptionalParams,
  ): Promise<WebPubSubGenerateClientTokenResponse> {
    return this.client.sendOperationRequest(
      { hub, options },
      generateClientTokenOperationSpec,
    );
  }

  /**
   * Remove filtered connections from multiple groups.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param groupsToRemove Target groups and connection filter.
   * @param options The options parameters.
   */
  removeConnectionsFromGroups(
    hub: string,
    groupsToRemove: RemoveFromGroupsRequest,
    options?: WebPubSubRemoveConnectionsFromGroupsOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { hub, groupsToRemove, options },
      removeConnectionsFromGroupsOperationSpec,
    );
  }

  /**
   * Broadcast content inside request body to all the connected client connections.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param contentType Upload file type
   * @param message The payload body.
   * @param options The options parameters.
   */
  sendToAll(
    hub: string,
    contentType: ContentType,
    message: coreRestPipeline.RequestBodyType,
    options?: WebPubSubSendToAll$binaryOptionalParams,
  ): Promise<void>;
  /**
   * Broadcast content inside request body to all the connected client connections.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param contentType Upload file type
   * @param message The payload body.
   * @param options The options parameters.
   */
  sendToAll(
    hub: string,
    contentType: "text/plain",
    message: string,
    options?: WebPubSubSendToAll$textOptionalParams,
  ): Promise<void>;
  /**
   * Broadcast content inside request body to all the connected client connections.
   * @param args Includes all the parameters for this operation.
   */
  sendToAll(
    ...args:
      | [
          string,
          ContentType,
          coreRestPipeline.RequestBodyType,
          WebPubSubSendToAll$binaryOptionalParams?,
        ]
      | [string, "text/plain", string, WebPubSubSendToAll$textOptionalParams?]
  ): Promise<void> {
    let operationSpec: coreClient.OperationSpec;
    let operationArguments: coreClient.OperationArguments;
    let options;
    if (
      args[1] === "application/json" ||
      args[1] === "application/octet-stream"
    ) {
      operationSpec = sendToAll$binaryOperationSpec;
      operationArguments = {
        hub: args[0],
        contentType: args[1],
        message: args[2],
        options: args[3],
      };
      options = args[3];
    } else if (args[1] === "text/plain") {
      operationSpec = sendToAll$textOperationSpec;
      operationArguments = {
        hub: args[0],
        contentType: args[1],
        message: args[2],
        options: args[3],
      };
      options = args[3];
    } else {
      throw new TypeError(
        `"contentType" must be a valid value but instead was "${args[1]}".`,
      );
    }
    operationArguments.options = options || {};
    return this.client.sendOperationRequest(operationArguments, operationSpec);
  }

  /**
   * Close the client connection.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param connectionId Target connection Id.
   * @param options The options parameters.
   */
  closeConnection(
    hub: string,
    connectionId: string,
    options?: WebPubSubCloseConnectionOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { hub, connectionId, options },
      closeConnectionOperationSpec,
    );
  }

  /**
   * Check if the connection with the given connectionId exists.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param connectionId The connection Id.
   * @param options The options parameters.
   */
  connectionExists(
    hub: string,
    connectionId: string,
    options?: WebPubSubConnectionExistsOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { hub, connectionId, options },
      connectionExistsOperationSpec,
    );
  }

  /**
   * Send content inside request body to the specific connection.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param connectionId The connection Id.
   * @param contentType Upload file type
   * @param message The payload body.
   * @param options The options parameters.
   */
  sendToConnection(
    hub: string,
    connectionId: string,
    contentType: ContentType,
    message: coreRestPipeline.RequestBodyType,
    options?: WebPubSubSendToConnection$binaryOptionalParams,
  ): Promise<void>;
  /**
   * Send content inside request body to the specific connection.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param connectionId The connection Id.
   * @param contentType Upload file type
   * @param message The payload body.
   * @param options The options parameters.
   */
  sendToConnection(
    hub: string,
    connectionId: string,
    contentType: "text/plain",
    message: string,
    options?: WebPubSubSendToConnection$textOptionalParams,
  ): Promise<void>;
  /**
   * Send content inside request body to the specific connection.
   * @param args Includes all the parameters for this operation.
   */
  sendToConnection(
    ...args:
      | [
          string,
          string,
          ContentType,
          coreRestPipeline.RequestBodyType,
          WebPubSubSendToConnection$binaryOptionalParams?,
        ]
      | [
          string,
          string,
          "text/plain",
          string,
          WebPubSubSendToConnection$textOptionalParams?,
        ]
  ): Promise<void> {
    let operationSpec: coreClient.OperationSpec;
    let operationArguments: coreClient.OperationArguments;
    let options;
    if (
      args[2] === "application/json" ||
      args[2] === "application/octet-stream"
    ) {
      operationSpec = sendToConnection$binaryOperationSpec;
      operationArguments = {
        hub: args[0],
        connectionId: args[1],
        contentType: args[2],
        message: args[3],
        options: args[4],
      };
      options = args[4];
    } else if (args[2] === "text/plain") {
      operationSpec = sendToConnection$textOperationSpec;
      operationArguments = {
        hub: args[0],
        connectionId: args[1],
        contentType: args[2],
        message: args[3],
        options: args[4],
      };
      options = args[4];
    } else {
      throw new TypeError(
        `"contentType" must be a valid value but instead was "${args[2]}".`,
      );
    }
    operationArguments.options = options || {};
    return this.client.sendOperationRequest(operationArguments, operationSpec);
  }

  /**
   * Remove a connection from all groups.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param connectionId Target connection Id.
   * @param options The options parameters.
   */
  removeConnectionFromAllGroups(
    hub: string,
    connectionId: string,
    options?: WebPubSubRemoveConnectionFromAllGroupsOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { hub, connectionId, options },
      removeConnectionFromAllGroupsOperationSpec,
    );
  }

  /**
   * Check if there are any client connections inside the given group
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param group Target group name, which length should be greater than 0 and less than 1025.
   * @param options The options parameters.
   */
  groupExists(
    hub: string,
    group: string,
    options?: WebPubSubGroupExistsOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { hub, group, options },
      groupExistsOperationSpec,
    );
  }

  /**
   * Close connections in the specific group.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param group Target group name, which length should be greater than 0 and less than 1025.
   * @param options The options parameters.
   */
  closeGroupConnections(
    hub: string,
    group: string,
    options?: WebPubSubCloseGroupConnectionsOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { hub, group, options },
      closeGroupConnectionsOperationSpec,
    );
  }

  /**
   * Send content inside request body to a group of connections.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param group Target group name, which length should be greater than 0 and less than 1025.
   * @param contentType Upload file type
   * @param message The payload body.
   * @param options The options parameters.
   */
  sendToGroup(
    hub: string,
    group: string,
    contentType: ContentType,
    message: coreRestPipeline.RequestBodyType,
    options?: WebPubSubSendToGroup$binaryOptionalParams,
  ): Promise<void>;
  /**
   * Send content inside request body to a group of connections.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param group Target group name, which length should be greater than 0 and less than 1025.
   * @param contentType Upload file type
   * @param message The payload body.
   * @param options The options parameters.
   */
  sendToGroup(
    hub: string,
    group: string,
    contentType: "text/plain",
    message: string,
    options?: WebPubSubSendToGroup$textOptionalParams,
  ): Promise<void>;
  /**
   * Send content inside request body to a group of connections.
   * @param args Includes all the parameters for this operation.
   */
  sendToGroup(
    ...args:
      | [
          string,
          string,
          ContentType,
          coreRestPipeline.RequestBodyType,
          WebPubSubSendToGroup$binaryOptionalParams?,
        ]
      | [
          string,
          string,
          "text/plain",
          string,
          WebPubSubSendToGroup$textOptionalParams?,
        ]
  ): Promise<void> {
    let operationSpec: coreClient.OperationSpec;
    let operationArguments: coreClient.OperationArguments;
    let options;
    if (
      args[2] === "application/json" ||
      args[2] === "application/octet-stream"
    ) {
      operationSpec = sendToGroup$binaryOperationSpec;
      operationArguments = {
        hub: args[0],
        group: args[1],
        contentType: args[2],
        message: args[3],
        options: args[4],
      };
      options = args[4];
    } else if (args[2] === "text/plain") {
      operationSpec = sendToGroup$textOperationSpec;
      operationArguments = {
        hub: args[0],
        group: args[1],
        contentType: args[2],
        message: args[3],
        options: args[4],
      };
      options = args[4];
    } else {
      throw new TypeError(
        `"contentType" must be a valid value but instead was "${args[2]}".`,
      );
    }
    operationArguments.options = options || {};
    return this.client.sendOperationRequest(operationArguments, operationSpec);
  }

  /**
   * List connections in a group.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param group Target group name, whose length should be greater than 0 and less than 1025.
   * @param options The options parameters.
   */
  private _listConnectionsInGroup(
    hub: string,
    group: string,
    options?: WebPubSubListConnectionsInGroupOptionalParams,
  ): Promise<WebPubSubListConnectionsInGroupResponse> {
    return this.client.sendOperationRequest(
      { hub, group, options },
      listConnectionsInGroupOperationSpec,
    );
  }

  /**
   * Remove a connection from the target group.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param group Target group name, which length should be greater than 0 and less than 1025.
   * @param connectionId Target connection Id.
   * @param options The options parameters.
   */
  removeConnectionFromGroup(
    hub: string,
    group: string,
    connectionId: string,
    options?: WebPubSubRemoveConnectionFromGroupOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { hub, group, connectionId, options },
      removeConnectionFromGroupOperationSpec,
    );
  }

  /**
   * Add a connection to the target group.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param group Target group name, which length should be greater than 0 and less than 1025.
   * @param connectionId Target connection Id
   * @param options The options parameters.
   */
  addConnectionToGroup(
    hub: string,
    group: string,
    connectionId: string,
    options?: WebPubSubAddConnectionToGroupOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { hub, group, connectionId, options },
      addConnectionToGroupOperationSpec,
    );
  }

  /**
   * Revoke permission for the connection.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param permission The permission: current supported actions are joinLeaveGroup and sendToGroup.
   * @param connectionId Target connection Id.
   * @param options The options parameters.
   */
  revokePermission(
    hub: string,
    permission: WebPubSubPermission,
    connectionId: string,
    options?: WebPubSubRevokePermissionOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { hub, permission, connectionId, options },
      revokePermissionOperationSpec,
    );
  }

  /**
   * Check if a connection has permission to the specified action.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param permission The permission: current supported actions are joinLeaveGroup and sendToGroup.
   * @param connectionId Target connection Id.
   * @param options The options parameters.
   */
  checkPermission(
    hub: string,
    permission: WebPubSubPermission,
    connectionId: string,
    options?: WebPubSubCheckPermissionOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { hub, permission, connectionId, options },
      checkPermissionOperationSpec,
    );
  }

  /**
   * Grant permission to the connection.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param permission The permission: current supported actions are joinLeaveGroup and sendToGroup.
   * @param connectionId Target connection Id.
   * @param options The options parameters.
   */
  grantPermission(
    hub: string,
    permission: WebPubSubPermission,
    connectionId: string,
    options?: WebPubSubGrantPermissionOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { hub, permission, connectionId, options },
      grantPermissionOperationSpec,
    );
  }

  /**
   * Check if there are any client connections connected for the given user.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param userId Target user Id.
   * @param options The options parameters.
   */
  userExists(
    hub: string,
    userId: string,
    options?: WebPubSubUserExistsOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { hub, userId, options },
      userExistsOperationSpec,
    );
  }

  /**
   * Close connections for the specific user.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param userId The user Id.
   * @param options The options parameters.
   */
  closeUserConnections(
    hub: string,
    userId: string,
    options?: WebPubSubCloseUserConnectionsOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { hub, userId, options },
      closeUserConnectionsOperationSpec,
    );
  }

  /**
   * Send content inside request body to the specific user.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param userId The user Id.
   * @param contentType Upload file type
   * @param message The payload body.
   * @param options The options parameters.
   */
  sendToUser(
    hub: string,
    userId: string,
    contentType: ContentType,
    message: coreRestPipeline.RequestBodyType,
    options?: WebPubSubSendToUser$binaryOptionalParams,
  ): Promise<void>;
  /**
   * Send content inside request body to the specific user.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param userId The user Id.
   * @param contentType Upload file type
   * @param message The payload body.
   * @param options The options parameters.
   */
  sendToUser(
    hub: string,
    userId: string,
    contentType: "text/plain",
    message: string,
    options?: WebPubSubSendToUser$textOptionalParams,
  ): Promise<void>;
  /**
   * Send content inside request body to the specific user.
   * @param args Includes all the parameters for this operation.
   */
  sendToUser(
    ...args:
      | [
          string,
          string,
          ContentType,
          coreRestPipeline.RequestBodyType,
          WebPubSubSendToUser$binaryOptionalParams?,
        ]
      | [
          string,
          string,
          "text/plain",
          string,
          WebPubSubSendToUser$textOptionalParams?,
        ]
  ): Promise<void> {
    let operationSpec: coreClient.OperationSpec;
    let operationArguments: coreClient.OperationArguments;
    let options;
    if (
      args[2] === "application/json" ||
      args[2] === "application/octet-stream"
    ) {
      operationSpec = sendToUser$binaryOperationSpec;
      operationArguments = {
        hub: args[0],
        userId: args[1],
        contentType: args[2],
        message: args[3],
        options: args[4],
      };
      options = args[4];
    } else if (args[2] === "text/plain") {
      operationSpec = sendToUser$textOperationSpec;
      operationArguments = {
        hub: args[0],
        userId: args[1],
        contentType: args[2],
        message: args[3],
        options: args[4],
      };
      options = args[4];
    } else {
      throw new TypeError(
        `"contentType" must be a valid value but instead was "${args[2]}".`,
      );
    }
    operationArguments.options = options || {};
    return this.client.sendOperationRequest(operationArguments, operationSpec);
  }

  /**
   * Remove a user from all groups.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param userId Target user Id.
   * @param options The options parameters.
   */
  removeUserFromAllGroups(
    hub: string,
    userId: string,
    options?: WebPubSubRemoveUserFromAllGroupsOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { hub, userId, options },
      removeUserFromAllGroupsOperationSpec,
    );
  }

  /**
   * Remove a user from the target group.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param group Target group name, which length should be greater than 0 and less than 1025.
   * @param userId Target user Id.
   * @param options The options parameters.
   */
  removeUserFromGroup(
    hub: string,
    group: string,
    userId: string,
    options?: WebPubSubRemoveUserFromGroupOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { hub, group, userId, options },
      removeUserFromGroupOperationSpec,
    );
  }

  /**
   * Add a user to the target group.
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param group Target group name, which length should be greater than 0 and less than 1025.
   * @param userId Target user Id.
   * @param options The options parameters.
   */
  addUserToGroup(
    hub: string,
    group: string,
    userId: string,
    options?: WebPubSubAddUserToGroupOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { hub, group, userId, options },
      addUserToGroupOperationSpec,
    );
  }

  /**
   * ListConnectionsInGroupNext
   * @param hub Target hub name, which should start with alphabetic characters and only contain
   *            alpha-numeric characters or underscore.
   * @param group Target group name, whose length should be greater than 0 and less than 1025.
   * @param nextLink The nextLink from the previous successful call to the ListConnectionsInGroup method.
   * @param options The options parameters.
   */
  private _listConnectionsInGroupNext(
    hub: string,
    group: string,
    nextLink: string,
    options?: WebPubSubListConnectionsInGroupNextOptionalParams,
  ): Promise<WebPubSubListConnectionsInGroupNextResponse> {
    return this.client.sendOperationRequest(
      { hub, group, nextLink, options },
      listConnectionsInGroupNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const addConnectionsToGroupsOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/:addToGroups",
  httpMethod: "POST",
  responses: {
    200: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubAddConnectionsToGroupsExceptionHeaders,
    },
  },
  requestBody: Parameters.groupsToAdd,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.hub],
  headerParameters: [Parameters.contentType, Parameters.accept],
  mediaType: "json",
  serializer,
};
const closeAllConnectionsOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/:closeConnections",
  httpMethod: "POST",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubCloseAllConnectionsExceptionHeaders,
    },
  },
  queryParameters: [
    Parameters.apiVersion,
    Parameters.excluded,
    Parameters.reason,
  ],
  urlParameters: [Parameters.endpoint, Parameters.hub],
  headerParameters: [Parameters.accept],
  serializer,
};
const generateClientTokenOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/:generateToken",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.ClientTokenResponse,
    },
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubGenerateClientTokenExceptionHeaders,
    },
  },
  queryParameters: [
    Parameters.apiVersion,
    Parameters.userId,
    Parameters.roles,
    Parameters.expirationTimeInMinutes,
    Parameters.groups,
    Parameters.clientType,
  ],
  urlParameters: [Parameters.endpoint, Parameters.hub],
  headerParameters: [Parameters.accept1],
  serializer,
};
const removeConnectionsFromGroupsOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/:removeFromGroups",
  httpMethod: "POST",
  responses: {
    200: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper:
        Mappers.WebPubSubRemoveConnectionsFromGroupsExceptionHeaders,
    },
  },
  requestBody: Parameters.groupsToRemove,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.hub],
  headerParameters: [Parameters.contentType, Parameters.accept],
  mediaType: "json",
  serializer,
};
const sendToAll$binaryOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/:send",
  httpMethod: "POST",
  responses: {
    202: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubSendToAllExceptionHeaders,
    },
  },
  requestBody: Parameters.message,
  queryParameters: [
    Parameters.apiVersion,
    Parameters.excludedConnections,
    Parameters.filter,
    Parameters.messageTtlSeconds,
  ],
  urlParameters: [Parameters.endpoint, Parameters.hub],
  headerParameters: [Parameters.contentType1, Parameters.accept2],
  mediaType: "binary",
  serializer,
};
const sendToAll$textOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/:send",
  httpMethod: "POST",
  responses: {
    202: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubSendToAllExceptionHeaders,
    },
  },
  requestBody: Parameters.message1,
  queryParameters: [
    Parameters.apiVersion,
    Parameters.excludedConnections,
    Parameters.filter,
    Parameters.messageTtlSeconds,
  ],
  urlParameters: [Parameters.endpoint, Parameters.hub],
  headerParameters: [Parameters.contentType2, Parameters.accept3],
  mediaType: "text",
  serializer,
};
const closeConnectionOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/connections/{connectionId}",
  httpMethod: "DELETE",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubCloseConnectionExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.reason],
  urlParameters: [Parameters.endpoint, Parameters.hub, Parameters.connectionId],
  headerParameters: [Parameters.accept],
  serializer,
};
const connectionExistsOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/connections/{connectionId}",
  httpMethod: "HEAD",
  responses: {
    200: {},
    404: {},
    default: {
      headersMapper: Mappers.WebPubSubConnectionExistsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.hub, Parameters.connectionId],
  serializer,
};
const sendToConnection$binaryOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/connections/{connectionId}/:send",
  httpMethod: "POST",
  responses: {
    202: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubSendToConnectionExceptionHeaders,
    },
  },
  requestBody: Parameters.message,
  queryParameters: [Parameters.apiVersion, Parameters.messageTtlSeconds],
  urlParameters: [Parameters.endpoint, Parameters.hub, Parameters.connectionId],
  headerParameters: [Parameters.contentType1, Parameters.accept2],
  mediaType: "binary",
  serializer,
};
const sendToConnection$textOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/connections/{connectionId}/:send",
  httpMethod: "POST",
  responses: {
    202: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubSendToConnectionExceptionHeaders,
    },
  },
  requestBody: Parameters.message1,
  queryParameters: [Parameters.apiVersion, Parameters.messageTtlSeconds],
  urlParameters: [Parameters.endpoint, Parameters.hub, Parameters.connectionId],
  headerParameters: [Parameters.contentType2, Parameters.accept3],
  mediaType: "text",
  serializer,
};
const removeConnectionFromAllGroupsOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/connections/{connectionId}/groups",
  httpMethod: "DELETE",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper:
        Mappers.WebPubSubRemoveConnectionFromAllGroupsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.hub, Parameters.connectionId],
  headerParameters: [Parameters.accept],
  serializer,
};
const groupExistsOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/groups/{group}",
  httpMethod: "HEAD",
  responses: {
    200: {},
    404: {},
    default: {
      headersMapper: Mappers.WebPubSubGroupExistsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.hub, Parameters.group],
  serializer,
};
const closeGroupConnectionsOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/groups/{group}/:closeConnections",
  httpMethod: "POST",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubCloseGroupConnectionsExceptionHeaders,
    },
  },
  queryParameters: [
    Parameters.apiVersion,
    Parameters.excluded,
    Parameters.reason,
  ],
  urlParameters: [Parameters.endpoint, Parameters.hub, Parameters.group],
  headerParameters: [Parameters.accept],
  serializer,
};
const sendToGroup$binaryOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/groups/{group}/:send",
  httpMethod: "POST",
  responses: {
    202: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubSendToGroupExceptionHeaders,
    },
  },
  requestBody: Parameters.message,
  queryParameters: [
    Parameters.apiVersion,
    Parameters.excludedConnections,
    Parameters.filter,
    Parameters.messageTtlSeconds,
  ],
  urlParameters: [Parameters.endpoint, Parameters.hub, Parameters.group],
  headerParameters: [Parameters.contentType1, Parameters.accept2],
  mediaType: "binary",
  serializer,
};
const sendToGroup$textOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/groups/{group}/:send",
  httpMethod: "POST",
  responses: {
    202: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubSendToGroupExceptionHeaders,
    },
  },
  requestBody: Parameters.message1,
  queryParameters: [
    Parameters.apiVersion,
    Parameters.excludedConnections,
    Parameters.filter,
    Parameters.messageTtlSeconds,
  ],
  urlParameters: [Parameters.endpoint, Parameters.hub, Parameters.group],
  headerParameters: [Parameters.contentType2, Parameters.accept3],
  mediaType: "text",
  serializer,
};
const listConnectionsInGroupOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/groups/{group}/connections",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.GroupMemberPagedValues,
    },
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubListConnectionsInGroupExceptionHeaders,
    },
  },
  queryParameters: [
    Parameters.apiVersion,
    Parameters.maxPageSize,
    Parameters.top,
    Parameters.continuationToken,
  ],
  urlParameters: [Parameters.endpoint, Parameters.hub, Parameters.group],
  headerParameters: [Parameters.accept],
  serializer,
};
const removeConnectionFromGroupOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/groups/{group}/connections/{connectionId}",
  httpMethod: "DELETE",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubRemoveConnectionFromGroupExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.hub,
    Parameters.connectionId,
    Parameters.group,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const addConnectionToGroupOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/groups/{group}/connections/{connectionId}",
  httpMethod: "PUT",
  responses: {
    200: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubAddConnectionToGroupExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.hub,
    Parameters.connectionId,
    Parameters.group,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const revokePermissionOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/permissions/{permission}/connections/{connectionId}",
  httpMethod: "DELETE",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubRevokePermissionExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.targetName],
  urlParameters: [
    Parameters.endpoint,
    Parameters.hub,
    Parameters.connectionId,
    Parameters.permission,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const checkPermissionOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/permissions/{permission}/connections/{connectionId}",
  httpMethod: "HEAD",
  responses: {
    200: {},
    404: {},
    default: {
      headersMapper: Mappers.WebPubSubCheckPermissionExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.targetName],
  urlParameters: [
    Parameters.endpoint,
    Parameters.hub,
    Parameters.connectionId,
    Parameters.permission,
  ],
  serializer,
};
const grantPermissionOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/permissions/{permission}/connections/{connectionId}",
  httpMethod: "PUT",
  responses: {
    200: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubGrantPermissionExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.targetName],
  urlParameters: [
    Parameters.endpoint,
    Parameters.hub,
    Parameters.connectionId,
    Parameters.permission,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const userExistsOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/users/{userId}",
  httpMethod: "HEAD",
  responses: {
    200: {},
    404: {},
    default: {
      headersMapper: Mappers.WebPubSubUserExistsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.hub, Parameters.userId1],
  serializer,
};
const closeUserConnectionsOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/users/{userId}/:closeConnections",
  httpMethod: "POST",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubCloseUserConnectionsExceptionHeaders,
    },
  },
  queryParameters: [
    Parameters.apiVersion,
    Parameters.excluded,
    Parameters.reason,
  ],
  urlParameters: [Parameters.endpoint, Parameters.hub, Parameters.userId1],
  headerParameters: [Parameters.accept],
  serializer,
};
const sendToUser$binaryOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/users/{userId}/:send",
  httpMethod: "POST",
  responses: {
    202: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubSendToUserExceptionHeaders,
    },
  },
  requestBody: Parameters.message,
  queryParameters: [
    Parameters.apiVersion,
    Parameters.filter,
    Parameters.messageTtlSeconds,
  ],
  urlParameters: [Parameters.endpoint, Parameters.hub, Parameters.userId1],
  headerParameters: [Parameters.contentType1, Parameters.accept2],
  mediaType: "binary",
  serializer,
};
const sendToUser$textOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/users/{userId}/:send",
  httpMethod: "POST",
  responses: {
    202: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubSendToUserExceptionHeaders,
    },
  },
  requestBody: Parameters.message1,
  queryParameters: [
    Parameters.apiVersion,
    Parameters.filter,
    Parameters.messageTtlSeconds,
  ],
  urlParameters: [Parameters.endpoint, Parameters.hub, Parameters.userId1],
  headerParameters: [Parameters.contentType2, Parameters.accept3],
  mediaType: "text",
  serializer,
};
const removeUserFromAllGroupsOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/users/{userId}/groups",
  httpMethod: "DELETE",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubRemoveUserFromAllGroupsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.hub, Parameters.userId1],
  headerParameters: [Parameters.accept],
  serializer,
};
const removeUserFromGroupOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/users/{userId}/groups/{group}",
  httpMethod: "DELETE",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubRemoveUserFromGroupExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.hub,
    Parameters.group,
    Parameters.userId1,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const addUserToGroupOperationSpec: coreClient.OperationSpec = {
  path: "/api/hubs/{hub}/users/{userId}/groups/{group}",
  httpMethod: "PUT",
  responses: {
    200: {},
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper: Mappers.WebPubSubAddUserToGroupExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.hub,
    Parameters.group,
    Parameters.userId1,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listConnectionsInGroupNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.GroupMemberPagedValues,
    },
    default: {
      bodyMapper: Mappers.ErrorDetail,
      headersMapper:
        Mappers.WebPubSubListConnectionsInGroupNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.hub,
    Parameters.group,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
